import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, Loader2 } from 'lucide-react';
import API from '../services/api';
import { AuthContext } from '../context/AuthContext';
import '../styles/Auth.css';

export default function Login() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState('');
  const { login }               = useContext(AuthContext);
  const navigate                = useNavigate();

  // role → route
  const roleRoute = {
    student: '/student',
    warden: '/warden',
    gatekeeper: '/gatekeeper',
    'vc-office': '/vc-office',
    admin: '/admin',
    superadmin: '/analytics',
    'guest-house': '/guest-house',
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const { data } = await API.post('/auth/login', {
        email: email.trim(),
        password,
      });

      login(data);
      const dest = roleRoute[data.user?.role] ?? '/';

      if (data.requiresPasswordChange) {
        sessionStorage.setItem('pw_change_required', '1');
        sessionStorage.setItem('pw_change_redirect', dest);
        navigate('/change-password-first');
        return;
      }

      navigate(dest);
    } catch (error) {
      const msg = error?.response?.data?.msg || error.message || 'Login failed';
      setErr(msg);
      if (error?.response?.status === 428) {
        sessionStorage.setItem('pw_change_required', '1');
        navigate('/change-password-first');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrapper simple">
      <div className="auth-card">
        {/* Title */}
        <div className="title-group">
          <h2>Sign in</h2>
          <p>Use your university account</p>
        </div>

        {/* Error */}
        {err && <div className="status-bar error">{err}</div>}

        {/* Form */}
        <form onSubmit={submit} className="auth-form">
          {/* Email */}
          <div className="input-field">
            <span className="input-icon" aria-hidden>
              <Mail size={18} />
            </span>
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
            />
          </div>

          {/* Password */}
          <div className="input-field">
            <span className="input-icon" aria-hidden>
              <Lock size={18} />
            </span>
            <input
              type={showPw ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              className="icon-btn"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? 'Hide password' : 'Show password'}
              title={showPw ? 'Hide password' : 'Show password'}
            >
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? <><Loader2 className="spin" size={18} /> Signing in…</> : 'Sign in'}
          </button>
        </form>

        {/* Links */}
        <div className="helper-links">
          <Link to="/forgot">Forgot password?</Link>
          <span className="dot" aria-hidden>•</span>
          <span>Don’t have an account? <Link to="/signup">Sign up</Link></span>
        </div>
      </div>
    </div>
  );
}
